import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowCounterClockwise,
  Archive,
  CheckCircle,
  Spinner,
  Trash,
  UserCircle,
  Users,
  ShieldCheck,
  ShieldWarning,
  ChatsCircle,
  Compass,
} from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { apiError } from "../lib/errors";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

function CoachesTab({ coaches, refresh }) {
  const onApprove = async (c) => {
    try {
      await api.post(`/admin/coaches/${c.id}/approve`);
      toast.success(`${c.name} approved.`);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Approve failed"));
    }
  };
  const onReject = async (c) => {
    if (!window.confirm(`Reject ${c.email}? This deletes the account.`)) return;
    try {
      await api.delete(`/admin/coaches/${c.id}`);
      toast.success(`${c.email} removed.`);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Reject failed"));
    }
  };

  if (!coaches.length) {
    return <div className="text-[#8A948E] text-sm bg-[#F3F0EA] rounded-xl px-4 py-6 text-center">No coaches yet.</div>;
  }

  return (
    <div className="space-y-2" data-testid="admin-coaches-list">
      {coaches.map((c) => {
        const pending = c.status === "pending_approval";
        const isAdmin = c.role === "admin";
        return (
          <div
            key={c.id}
            data-testid={`admin-coach-row-${c.id}`}
            className="flex items-center justify-between gap-3 bg-white border border-[#E8E4DB] rounded-xl px-4 py-3 hover:border-[#1E3A2B]/40 transition-colors"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  pending
                    ? "bg-[#C77D5E]/15 text-[#C77D5E]"
                    : isAdmin
                    ? "bg-[#1E3A2B] text-white"
                    : "bg-[#1E3A2B]/10 text-[#1E3A2B]"
                }`}
              >
                {isAdmin ? <ShieldCheck size={18} weight="duotone" /> : pending ? <ShieldWarning size={18} weight="duotone" /> : <UserCircle size={18} weight="duotone" />}
              </div>
              <div className="min-w-0">
                <div className="font-medium text-[#121815] truncate flex items-center gap-2">
                  {c.name}
                  {isAdmin && <span className="text-[10px] uppercase tracking-wider bg-[#1E3A2B] text-white px-2 py-0.5 rounded-full">Admin</span>}
                  {pending && <span className="text-[10px] uppercase tracking-wider bg-[#C77D5E] text-white px-2 py-0.5 rounded-full">Pending</span>}
                </div>
                <div className="text-xs text-[#8A948E] mt-0.5 truncate">
                  {c.email} · joined {fmtDate(c.created_at)} · {c.assigned_leader_count} leader{c.assigned_leader_count === 1 ? "" : "s"} assigned
                </div>
              </div>
            </div>
            {pending ? (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={() => onApprove(c)}
                  data-testid={`admin-approve-${c.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#1E3A2B] text-white px-3 py-1.5 rounded-lg hover:bg-[#2C4C3B] text-sm"
                >
                  <CheckCircle size={14} weight="duotone" /> Approve
                </button>
                <button
                  onClick={() => onReject(c)}
                  data-testid={`admin-reject-${c.id}`}
                  className="inline-flex items-center gap-1.5 bg-[#F3F0EA] text-[#C77D5E] px-3 py-1.5 rounded-lg hover:bg-[#E8E4DB] text-sm"
                >
                  <Trash size={14} weight="duotone" /> Reject
                </button>
              </div>
            ) : !isAdmin ? (
              <button
                onClick={() => onReject(c)}
                data-testid={`admin-remove-${c.id}`}
                className="text-[#8A948E] hover:text-[#C77D5E] p-2 rounded-lg hover:bg-[#F3F0EA]"
                aria-label="Remove coach"
                title="Remove coach (must have no assigned leaders)"
              >
                <Trash size={16} weight="duotone" />
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function LeaderRow({ leader, approvedCoaches, onAssign, onArchive, onDelete, onUnarchive }) {
  const [savingTo, setSavingTo] = useState(null);
  const change = async (newCoachId) => {
    setSavingTo(newCoachId || "__none__");
    try {
      await onAssign(leader, newCoachId || null);
    } finally {
      setSavingTo(null);
    }
  };
  const isArchived = !!leader.archived_at;
  return (
    <div
      data-testid={`admin-leader-row-${leader.id}`}
      className={`flex items-center justify-between gap-3 bg-white border rounded-xl px-4 py-3 transition-colors ${
        isArchived ? "border-[#E8E4DB] opacity-70" : "border-[#E8E4DB] hover:border-[#1E3A2B]/40"
      }`}
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="w-9 h-9 rounded-full bg-[#1E3A2B]/10 text-[#1E3A2B] flex items-center justify-center flex-shrink-0">
          <UserCircle size={18} weight="duotone" />
        </div>
        <div className="min-w-0">
          <div className="font-medium text-[#121815] truncate flex items-center gap-2">
            {leader.name || leader.email}
            {isArchived && <span className="text-[10px] uppercase tracking-wider bg-[#8A948E] text-white px-2 py-0.5 rounded-full">Archived</span>}
          </div>
          <div className="text-xs text-[#8A948E] mt-0.5 truncate flex flex-wrap gap-x-2 gap-y-0.5">
            <span>{leader.email}</span>
            <span>· joined {fmtDate(leader.created_at)}</span>
            <span className="inline-flex items-center gap-1">
              <ChatsCircle size={11} weight="duotone" /> {leader.thread_count}
            </span>
            <span className="inline-flex items-center gap-1">
              <Compass size={11} weight="duotone" /> {leader.walkthrough_count}
            </span>
          </div>
        </div>
      </div>
      {!isArchived ? (
        <select
          value={leader.assigned_coach_id || ""}
          onChange={(e) => change(e.target.value)}
          disabled={savingTo !== null}
          data-testid={`admin-assign-select-${leader.id}`}
          className="bg-white border border-[#E8E4DB] focus:border-[#1E3A2B] focus:outline-none rounded-lg px-3 py-2 text-sm flex-shrink-0 max-w-[220px]"
        >
          <option value="">— Unassigned —</option>
          {approvedCoaches.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name} {c.role === "admin" ? "(admin)" : ""}
            </option>
          ))}
        </select>
      ) : null}
      <div className="flex items-center gap-1 flex-shrink-0">
        {isArchived ? (
          <button
            onClick={() => onUnarchive(leader)}
            data-testid={`admin-leader-unarchive-${leader.id}`}
            title="Restore"
            className="text-[#8A948E] hover:text-[#1E3A2B] p-2 rounded-lg hover:bg-[#F3F0EA]"
            aria-label="Restore leader"
          >
            <ArrowCounterClockwise size={16} weight="duotone" />
          </button>
        ) : (
          <button
            onClick={() => onArchive(leader)}
            data-testid={`admin-leader-archive-${leader.id}`}
            title="Archive"
            className="text-[#8A948E] hover:text-[#1E3A2B] p-2 rounded-lg hover:bg-[#F3F0EA]"
            aria-label="Archive leader"
          >
            <Archive size={16} weight="duotone" />
          </button>
        )}
        <button
          onClick={() => onDelete(leader)}
          data-testid={`admin-leader-delete-${leader.id}`}
          title="Delete leader and all their data"
          className="text-[#8A948E] hover:text-[#C77D5E] p-2 rounded-lg hover:bg-[#F3F0EA]"
          aria-label="Delete leader"
        >
          <Trash size={16} weight="duotone" />
        </button>
      </div>
    </div>
  );
}

function LeadersTab({ leaders, coaches, archivedCount, includeArchived, setIncludeArchived, refresh }) {
  const approvedCoaches = useMemo(
    () => coaches.filter((c) => c.status === "approved"),
    [coaches],
  );
  const [filter, setFilter] = useState("all"); // all | unassigned | active | archived
  const visible = useMemo(() => {
    if (filter === "archived") return leaders.filter((l) => !!l.archived_at);
    const live = leaders.filter((l) => !l.archived_at);
    if (filter === "unassigned") return live.filter((l) => !l.assigned_coach_id);
    if (filter === "active") return live.filter((l) => (l.thread_count || 0) > 0 || (l.walkthrough_count || 0) > 0);
    return live;
  }, [leaders, filter]);

  const onAssign = async (leader, coachId) => {
    try {
      await api.post(`/admin/leaders/${leader.id}/assign`, { coach_id: coachId });
      toast.success(coachId ? "Assigned." : "Unassigned.");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Assign failed"));
    }
  };
  const onArchive = async (leader) => {
    try {
      await api.post(`/admin/leaders/${leader.id}/archive`);
      toast.success(`${leader.email} archived.`, {
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/admin/leaders/${leader.id}/unarchive`);
              toast.success("Restored.");
              refresh();
            } catch {
              toast.error("Could not undo.");
            }
          },
        },
      });
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Archive failed"));
    }
  };
  const onUnarchive = async (leader) => {
    try {
      await api.post(`/admin/leaders/${leader.id}/unarchive`);
      toast.success("Restored.");
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Restore failed"));
    }
  };
  const onDelete = async (leader) => {
    const confirmText = window.prompt(
      `This permanently deletes ${leader.email} and ALL their data:\n` +
      `  · ${leader.thread_count} thread${leader.thread_count === 1 ? "" : "s"}\n` +
      `  · ${leader.walkthrough_count} walkthrough${leader.walkthrough_count === 1 ? "" : "s"}\n` +
      `  · all messages, videos, and stored files\n\n` +
      `This cannot be undone. Type DELETE to confirm.`,
    );
    if (confirmText !== "DELETE") return;
    try {
      const { data } = await api.delete(`/admin/leaders/${leader.id}`);
      toast.success(`Deleted ${leader.email} and ${data.threads_deleted} thread${data.threads_deleted === 1 ? "" : "s"}.`);
      refresh();
    } catch (err) {
      toast.error(apiError(err, "Delete failed"));
    }
  };

  const liveLeaders = leaders.filter((l) => !l.archived_at);
  const counts = {
    all: liveLeaders.length,
    unassigned: liveLeaders.filter((l) => !l.assigned_coach_id).length,
    active: liveLeaders.filter((l) => (l.thread_count || 0) > 0 || (l.walkthrough_count || 0) > 0).length,
    archived: archivedCount,
  };

  return (
    <>
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        {[
          { v: "all", label: `All · ${counts.all}` },
          { v: "unassigned", label: `Unassigned · ${counts.unassigned}` },
          { v: "active", label: `Engaged · ${counts.active}` },
        ].map((o) => (
          <button
            key={o.v}
            onClick={() => setFilter(o.v)}
            data-testid={`admin-leader-filter-${o.v}`}
            className={`px-3 py-1.5 rounded-full text-xs ${
              filter === o.v
                ? "bg-[#1E3A2B] text-white"
                : "bg-[#F3F0EA] text-[#4A524C] hover:bg-[#E8E4DB]"
            }`}
          >
            {o.label}
          </button>
        ))}
        <button
          onClick={() => {
            const next = !includeArchived;
            setIncludeArchived(next);
            setFilter(next ? "archived" : "all");
          }}
          data-testid="admin-leader-filter-archived"
          className={`px-3 py-1.5 rounded-full text-xs inline-flex items-center gap-1 ${
            filter === "archived"
              ? "bg-[#1E3A2B] text-white"
              : "bg-[#F3F0EA] text-[#4A524C] hover:bg-[#E8E4DB]"
          }`}
        >
          <Archive size={11} weight="duotone" /> Archived · {counts.archived}
        </button>
      </div>
      {visible.length === 0 ? (
        <div className="text-[#8A948E] text-sm bg-[#F3F0EA] rounded-xl px-4 py-6 text-center">
          No leaders match this filter.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-leaders-list">
          {visible.map((l) => (
            <LeaderRow
              key={l.id}
              leader={l}
              approvedCoaches={approvedCoaches}
              onAssign={onAssign}
              onArchive={onArchive}
              onUnarchive={onUnarchive}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}
    </>
  );
}

export default function CoachAdmin() {
  const { coach } = useAuth();
  const [tab, setTab] = useState("coaches");
  const [coaches, setCoaches] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [archivedCount, setArchivedCount] = useState(0);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/admin/coaches"),
        api.get(`/admin/leaders${includeArchived ? "?include_archived=true" : ""}`),
      ]);
      setCoaches(c.coaches || []);
      setLeaders(l.leaders || []);
      setArchivedCount(l.archived_count || 0);
    } catch (err) {
      toast.error(apiError(err, "Could not load admin data"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (coach && coach.role === "admin") refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coach, includeArchived]);

  if (coach === null) {
    return (
      <div className="text-center text-[#8A948E] py-16 flex items-center justify-center gap-2">
        <Spinner size={16} className="animate-spin" /> Loading…
      </div>
    );
  }
  if (!coach) return <Navigate to="/coach/login" state={{ from: "/coach/admin" }} replace />;
  if (coach.role !== "admin") return <Navigate to="/coach/dashboard" replace />;

  const pendingCount = coaches.filter((c) => c.status === "pending_approval").length;
  const unassignedCount = leaders.filter((l) => !l.assigned_coach_id).length;

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-8 py-12 lg:py-16">
      <div className="anim-in delay-1">
        <Link
          to="/coach/dashboard"
          className="inline-flex items-center gap-1.5 text-sm text-[#8A948E] hover:text-[#1E3A2B] mb-3"
        >
          <ArrowLeft size={14} weight="bold" /> Dashboard
        </Link>
        <div className="label-kicker" style={{ color: "#1E3A2B" }}>Admin Console</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1E3A2B] mt-3 tracking-tight leading-tight">
          Coaches & Leaders.
        </h1>
        <p className="text-[#4A524C] mt-3 leading-relaxed">
          Approve new coaches. Assign each registered leader to whoever should support them.
        </p>
        <Link
          to="/coach/admin/feedback"
          data-testid="admin-feedback-link"
          className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#C77D5E] hover:text-[#1E3A2B] font-medium"
        >
          → Feedback inbox
        </Link>
      </div>

      <div className="mt-7 flex flex-wrap gap-2 anim-in delay-2">
        <button
          onClick={() => setTab("coaches")}
          data-testid="admin-tab-coaches"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
            tab === "coaches" ? "bg-[#1E3A2B] text-white" : "bg-[#F3F0EA] text-[#4A524C] hover:bg-[#E8E4DB]"
          }`}
        >
          <ShieldCheck size={14} weight="duotone" /> Coaches · {coaches.length}
          {pendingCount > 0 && (
            <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1 ${
              tab === "coaches" ? "bg-white text-[#1E3A2B]" : "bg-[#C77D5E] text-white"
            }`}>{pendingCount}</span>
          )}
        </button>
        <button
          onClick={() => setTab("leaders")}
          data-testid="admin-tab-leaders"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-all ${
            tab === "leaders" ? "bg-[#1E3A2B] text-white" : "bg-[#F3F0EA] text-[#4A524C] hover:bg-[#E8E4DB]"
          }`}
        >
          <Users size={14} weight="duotone" /> Leaders · {leaders.length}
          {unassignedCount > 0 && (
            <span className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-bold px-1 ${
              tab === "leaders" ? "bg-white text-[#1E3A2B]" : "bg-[#C77D5E] text-white"
            }`}>{unassignedCount}</span>
          )}
        </button>
      </div>

      <div className="mt-6 anim-in delay-3">
        {loading ? (
          <div className="text-[#8A948E] text-sm flex items-center gap-2">
            <Spinner size={14} className="animate-spin" /> Loading…
          </div>
        ) : tab === "coaches" ? (
          <CoachesTab coaches={coaches} refresh={refresh} />
        ) : (
          <LeadersTab
            leaders={leaders}
            coaches={coaches}
            archivedCount={archivedCount}
            includeArchived={includeArchived}
            setIncludeArchived={setIncludeArchived}
            refresh={refresh}
          />
        )}
      </div>
    </div>
  );
}
